import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  CircleX,
  Copy,
  FileText,
  Gavel,
  Info,
  Loader2,
  Scale,
  Share2,
  Sparkles,
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../auth/firebaseConfig';
import { rentIncreaseCounties, toTitleCase } from '../../data/rentIncreaseNotice';
import RentReviewCalculator from '../../components/property-tools/RentReviewCalculator';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';

interface FaqItem {
  question: string;
  answer: string;
}

interface RentIncreaseNoticeReport {
  id: string;
  slug: string;
  county_slug: string;
  county_name: string;
  tax_year: number;
  inputs: {
    current_rent: number;
    date_rent_last_set: string;
    proposed_new_rent: number;
    notice_issue_date: string;
    proposed_effective_date: string;
  };
  calculations: {
    cap_percent_per_year: number;
    months_elapsed: number;
    years_elapsed: number;
    cap_percent_applied: number;
    max_allowed_increase: number;
    max_allowed_rent: number;
    proposed_rent_difference: number;
  };
  compliance_verdict: {
    key: string;
    label: string;
    statement: string;
    over_by: number;
  };
  seo_content: {
    h1: string;
    meta_title: string;
    meta_desc: string;
    snippet_hook: string;
    keywords: string[];
    faq: FaqItem[];
  };
  ctas: {
    rent: { label: string; path: string };
    compliance_hub: { label: string; path: string };
  };
  notice_template: {
    text: string;
    legal_disclaimer: string;
  };
  ai_enrichment?: {
    local_compliance_landscape?: string;
    notice_language_tips?: string[];
    dispute_prevention_checklist?: string[];
    documentation_packlist?: string[];
    common_landlord_mistakes?: string[];
    tenant_fairness_notes?: string[];
    county_watchouts?: string[];
    escalation_path?: Array<{ step: string; when_to_use: string; expected_outcome: string }>;
    social_share_snippets?: Array<{ label: string; text: string }>;
    faq_additions?: FaqItem[];
  } | null;
  schema?: unknown;
  generated_at?: string;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const formatDate = (value: string) => {
  if (!value) return 'n/a';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-IE', { year: 'numeric', month: 'short', day: 'numeric' });
};

const RentIncreaseNoticeCountyPage: React.FC = () => {
  const { county: countyParam = '' } = useParams<{ county: string }>();
  const county = countyParam.toLowerCase().trim();
  const countySupported = rentIncreaseCounties.includes(county);

  const [report, setReport] = useState<RentIncreaseNoticeReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [calculatorCompleted, setCalculatorCompleted] = useState(false);

  useEffect(() => {
    if (!countySupported) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const id = `rent-increase-notice-generator-${county}`;
        const snapshot = await getDoc(doc(db, 'rentIncreaseNoticeReports', id));
        if (!snapshot.exists()) {
          setError('Rent increase report not found for this county.');
          return;
        }
        setReport(snapshot.data() as RentIncreaseNoticeReport);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Unable to load report.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [county, countySupported]);

  const countyName = report?.county_name || toTitleCase(county);
  const canonicalUrl = `https://rentsmart.ie/tools/rent-increase-notice-generator-${county}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const mergedFaq = useMemo(() => {
    const allFaq = [
      ...(report?.seo_content.faq || []),
      ...(report?.ai_enrichment?.faq_additions || []),
    ];
    const seen = new Set<string>();

    return allFaq.filter((item) => {
      const key = `${item.question.trim().toLowerCase()}|${item.answer.trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [report]);

  if (!countySupported) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-xl rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <h1 className="text-2xl font-black text-slate-900 mb-3">County Page Unavailable</h1>
          <p className="text-slate-600 mb-5">Open the full generator hub and choose a supported county.</p>
          <Link to="/tools/rent-increase-notice-generator" className="no-underline inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-700">
            Open Compliance Hub <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
        <p className="font-bold text-slate-500">Loading county compliance report...</p>
      </div>
    );
  }

  if (!report || error) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
          <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-3">Report Unavailable</h1>
          <p className="text-slate-600 mb-8">{error || 'County report unavailable.'}</p>
          <Link to="/tools/rent-increase-notice-generator" className="inline-block rounded-2xl bg-emerald-600 text-white px-8 py-4 font-black hover:bg-emerald-700 no-underline">
            Browse County Tools
          </Link>
        </div>
      </div>
    );
  }

  const overLimit = report.compliance_verdict.over_by > 0;
  const shareText = `Check rent increase compliance in ${countyName} with this RentSmart RPZ notice report.`;
  const checklist = report.ai_enrichment?.dispute_prevention_checklist || [];
  const timeline = report.inputs;
  const calculations = report.calculations;

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50 text-slate-900">
      <Helmet>
        <title>{report.seo_content.meta_title}</title>
        <meta name="description" content={report.seo_content.meta_desc} />
        <meta name="keywords" content={(report.seo_content.keywords || []).join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        {report.schema ? <script type="application/ld+json">{JSON.stringify(report.schema)}</script> : null}
      </Helmet>

      <header className="bg-slate-900 text-white pt-12 pb-20 overflow-hidden relative">
        <div className="absolute top-0 right-0 h-80 w-80 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider mb-4">
                <Scale className="h-4 w-4" /> RPZ Rent Notice Check {report.tax_year}
              </div>
              <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
                {report.seo_content.h1}
              </h1>
              <p className="text-slate-200 text-lg leading-relaxed max-w-3xl">
                {report.seo_content.snippet_hook}
              </p>
            </div>
            <div className="hidden lg:block w-full lg:w-[380px] rounded-3xl border border-white/20 bg-white/10 p-6 backdrop-blur">
              <p className="text-xs uppercase tracking-widest text-slate-300 font-black mb-2">
                Sample Case Compliance Verdict
              </p>
              <p className="text-3xl font-black mb-3">{report.compliance_verdict.label}</p>
              <p className="text-slate-200 text-sm leading-relaxed">
                {report.compliance_verdict.statement}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 -mt-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <section className="lg:col-span-8 space-y-8">
            <div className="rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-sm mt-16">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <p className="text-[10px] uppercase tracking-[0.16em] font-black text-slate-400">
                  Core Rent Math
                </p>
                <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-sky-700">
                  Sample Case
                </span>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                These values are pre-filled from the {countyName} report scenario to show a worked example.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-2">
                    Current Rent
                  </p>
                  <p className="text-2xl font-black text-slate-900">
                    {formatCurrency(timeline.current_rent)}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-2">
                    Proposed Rent
                  </p>
                  <p className="text-2xl font-black text-slate-900">
                    {formatCurrency(timeline.proposed_new_rent)}
                  </p>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-emerald-700 mb-2">
                    Max Allowed Rent
                  </p>
                  <p className="text-2xl font-black text-emerald-800">
                    {formatCurrency(calculations.max_allowed_rent)}
                  </p>
                </div>
                <div
                  className={`rounded-2xl border p-4 ${
                    overLimit
                      ? 'border-amber-200 bg-amber-50'
                      : 'border-emerald-200 bg-emerald-50'
                  }`}
                >
                  <p
                    className={`text-[10px] uppercase tracking-wider font-black mb-2 ${
                      overLimit ? 'text-amber-700' : 'text-emerald-700'
                    }`}
                  >
                    Difference
                  </p>
                  <p
                    className={`text-2xl font-black ${
                      overLimit ? 'text-amber-800' : 'text-emerald-800'
                    }`}
                  >
                    {formatCurrency(Math.abs(calculations.proposed_rent_difference))}
                  </p>
                </div>
              </div>

              <div
                className={`mt-6 rounded-2xl border p-5 ${
                  overLimit ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {overLimit ? (
                    <CircleX className="h-5 w-5 mt-0.5 text-amber-700" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 mt-0.5 text-emerald-700" />
                  )}
                  <div>
                    <p className="text-xl font-black text-slate-900 mb-1">
                      {overLimit ? 'Action Needed Before Issuing Notice' : 'Within Calculated Limit'}
                    </p>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {report.compliance_verdict.statement}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-sm">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <p className="text-[10px] uppercase tracking-[0.16em] font-black text-slate-400">
                  Notice Timeline
                </p>
                <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-sky-700">
                  Sample Case
                </span>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-black text-slate-500 mb-1">Rent Last Set</p>
                  <p className="text-base font-bold text-slate-900">
                    {formatDate(timeline.date_rent_last_set)}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-black text-slate-500 mb-1">Notice Issued</p>
                  <p className="text-base font-bold text-slate-900">
                    {formatDate(timeline.notice_issue_date)}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-black text-slate-500 mb-1">Effective Date</p>
                  <p className="text-base font-bold text-slate-900">
                    {formatDate(timeline.proposed_effective_date)}
                  </p>
                </div>
              </div>
              <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Annual Cap
                  </p>
                  <p className="font-black text-slate-900">{calculations.cap_percent_per_year}%</p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Time Elapsed
                  </p>
                  <p className="font-black text-slate-900">{calculations.months_elapsed} months</p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Cap Applied
                  </p>
                  <p className="font-black text-slate-900">{calculations.cap_percent_applied}%</p>
                </div>
              </div>
            </div>

            <div className="rounded-[2.5rem] bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 border border-slate-700 p-6 md:p-8 shadow-xl">
              <p className="text-[10px] uppercase tracking-[0.2em] font-black text-emerald-300 mb-2">
                Draft Notice
              </p>
              <h2 className="text-2xl font-black text-white mb-5 flex items-center gap-2">
                <FileText className="h-5 w-5 text-emerald-300" /> Editable Template
              </h2>
              <textarea
                readOnly
                value={report.notice_template.text}
                className="w-full min-h-[260px] rounded-2xl border border-white/15 bg-white/10 p-4 text-sm text-slate-100"
              />
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-slate-300">{report.notice_template.legal_disclaimer}</p>
                <button
                  type="button"
                  onClick={async () => {
                    await navigator.clipboard.writeText(report.notice_template.text);
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                  }}
                  className="inline-flex items-center gap-2 rounded-xl bg-white text-slate-900 px-4 py-2.5 font-black text-sm hover:bg-slate-100"
                >
                  <Copy className="h-4 w-4" /> {copied ? 'Copied' : 'Copy Notice Text'}
                </button>
              </div>
            </div>

            <div className="rounded-3xl bg-white border border-slate-100 p-4 md:p-6 shadow-sm">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-5 mb-4">
                <p className="text-[10px] uppercase tracking-[0.16em] font-black text-slate-500 mb-2">
                  How To Use This Section
                </p>
                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <p className="font-black text-slate-900 mb-1">1. Review Sample Case</p>
                    <p className="text-slate-600">
                      Use the sample cards above to understand the Dublin report example.
                    </p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                    <p className="font-black text-emerald-900 mb-1">2. Run Your Own Case</p>
                    <p className="text-emerald-900/90">
                      Enter your rent and dates below to calculate your own maximum permitted rent.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] uppercase tracking-[0.16em] font-black text-slate-400 mb-2 px-2">
                Live Calculator
              </p>
              <RentReviewCalculator
                onCalculationComplete={() => setCalculatorCompleted(true)}
              />
            </div>

            {calculatorCompleted && (
              <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6 md:p-8">
                <p className="text-[10px] uppercase tracking-[0.16em] font-black text-emerald-700 mb-2">
                  Landlord Toolkit
                </p>
                <h2 className="text-2xl font-black text-emerald-900 mb-3">
                  Never Miss the Next Rent Review Window
                </h2>
                <p className="text-sm text-emerald-900/90 leading-relaxed mb-5">
                  Use RentSmart Landlord Toolkit to get email alerts when a rent increase may
                  become possible and when the next rent review is due, so you can plan notices
                  and stay compliant.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-5">
                  <div className="rounded-xl border border-emerald-200 bg-white/80 px-4 py-3 text-sm font-semibold text-emerald-900">
                    Email alerts for potential increase eligibility
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-white/80 px-4 py-3 text-sm font-semibold text-emerald-900">
                    Next rent review due reminders by email
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/landlord-toolkit"
                    className="no-underline inline-flex items-center justify-center rounded-xl bg-emerald-700 px-5 py-3 text-sm font-black text-white hover:bg-emerald-800"
                  >
                    Open Landlord Toolkit <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                
                </div>
              </div>
            )}

            {!!report.ai_enrichment?.local_compliance_landscape && (
              <div className="rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-sm">
                <h2 className="text-2xl font-black mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-sky-600" /> {countyName} Compliance Context
                </h2>
                <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                  {report.ai_enrichment.local_compliance_landscape}
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {!!report.ai_enrichment?.notice_language_tips?.length && (
                <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                  <h3 className="text-lg font-black text-emerald-900 mb-3">Notice Language Tips</h3>
                  <ul className="space-y-2">
                    {report.ai_enrichment.notice_language_tips.map((item, idx) => (
                      <li key={`${item}-${idx}`} className="text-sm text-emerald-900/90">
                        • {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!!report.ai_enrichment?.county_watchouts?.length && (
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5">
                  <h3 className="text-lg font-black text-amber-900 mb-3">County Watchouts</h3>
                  <ul className="space-y-2">
                    {report.ai_enrichment.county_watchouts.map((item, idx) => (
                      <li key={`${item}-${idx}`} className="text-sm text-amber-900/90">
                        • {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {!!report.ai_enrichment?.escalation_path?.length && (
              <div className="rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-sm">
                <h2 className="text-2xl font-black mb-4 flex items-center gap-2">
                  <Gavel className="h-5 w-5 text-slate-700" /> If a Dispute Happens
                </h2>
                <div className="space-y-3">
                  {report.ai_enrichment.escalation_path.map((item, idx) => (
                    <div
                      key={`${item.step}-${idx}`}
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <p className="text-xs uppercase tracking-wider font-black text-slate-500 mb-1">
                        Step {idx + 1}
                      </p>
                      <h3 className="font-black text-slate-900 mb-1">{item.step}</h3>
                      <p className="text-sm text-slate-700 mb-1">{item.when_to_use}</p>
                      <p className="text-xs text-slate-500 font-semibold">
                        Expected outcome: {item.expected_outcome}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!!mergedFaq.length && (
              <div className="rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-sm">
                <h2 className="text-2xl font-black mb-4">Frequently Asked Questions</h2>
                <div className="space-y-3">
                  {mergedFaq.map((item, idx) => (
                    <details
                      key={`${item.question}-${idx}`}
                      className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <summary className="font-bold text-slate-800 cursor-pointer">
                        {item.question}
                      </summary>
                      <p className="text-sm text-slate-600 mt-3 leading-relaxed">{item.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black mb-4">Official RTB Links</h2>
              <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                Use these official Residential Tenancies Board resources to verify calculations,
                notice rules, and dispute procedures.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <a
                  href="https://rtb.ie/compliance/check-rpz-compliance/how-rent-pressure-zones-rpzs-work/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-underline rounded-2xl border border-slate-200 bg-slate-50 p-4 hover:bg-slate-100 transition-colors"
                >
                  <p className="text-sm font-black text-slate-900 mb-1">RTB: Rent Pressure Zones</p>
                  <p className="text-xs text-slate-600">Overview of RPZ rules and rent-setting limits.</p>
                </a>
                <a
                  href="https://rtb.ie/compliance/check-rpz-compliance/rpz-calculator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-underline rounded-2xl border border-slate-200 bg-slate-50 p-4 hover:bg-slate-100 transition-colors"
                >
                  <p className="text-sm font-black text-slate-900 mb-1">RTB: RPZ Calculator</p>
                  <p className="text-xs text-slate-600">Check the official allowable rent increase.</p>
                </a>
                <a
                  href="https://www.rtb.ie/registration-and-compliance/setting-and-reviewing-rent"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-underline rounded-2xl border border-slate-200 bg-slate-50 p-4 hover:bg-slate-100 transition-colors"
                >
                  <p className="text-sm font-black text-slate-900 mb-1">RTB: Setting & Reviewing Rent</p>
                  <p className="text-xs text-slate-600">Official guidance for rent reviews and records.</p>
                </a>
                <a
                  href="https://www.rtb.ie/dispute-resolution-services"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-underline rounded-2xl border border-slate-200 bg-slate-50 p-4 hover:bg-slate-100 transition-colors"
                >
                  <p className="text-sm font-black text-slate-900 mb-1">RTB: Dispute Resolution</p>
                  <p className="text-xs text-slate-600">How to escalate and resolve tenancy disputes.</p>
                </a>
              </div>
            </div>

            <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 md:p-8">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-700 mt-0.5" />
                <div>
                  <h2 className="text-xl font-black text-amber-900 mb-2">Important Disclaimer</h2>
                  <p className="text-sm text-amber-900/90 leading-relaxed">
                    {report.notice_template.legal_disclaimer} RentSmart provides informational
                    estimates and drafting guidance only. Always verify notice validity against
                    current RTB requirements before issuing a rent review.
                  </p>
                </div>
              </div>
            </div>

            <PseoEmailCapture
              pageType="rent-increase-notice"
              pageSlug={report.slug}
              locationName={countyName}
              contextHeadline={`Get RPZ Compliance Updates For ${countyName}`}
              contextReason={`Receive Irish rental compliance and market updates relevant to ${countyName}, including notice-rule changes and practical guidance tailored for renters, landlords, and agents.`}
            />
          </section>

          <aside className="lg:col-span-4 space-y-6">
            <div className="sticky top-8 space-y-6">
              <div className="rounded-3xl bg-emerald-700 p-6 text-white shadow-xl">
                <h3 className="text-2xl font-black mb-3">{report.ctas.rent.label}</h3>
                <p className="text-emerald-100 text-sm mb-5 leading-relaxed">
                  Compare listings against this scenario to avoid proposing rent above the
                  calculated RPZ range.
                </p>
                <Link
                  to={report.ctas.rent.path}
                  className="no-underline inline-flex w-full items-center justify-between rounded-xl bg-white text-emerald-700 px-4 py-3 font-black"
                >
                  Browse Rentals <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="rounded-3xl bg-sky-800 p-6 text-white shadow-xl">
                <h3 className="text-2xl font-black mb-3">{report.ctas.compliance_hub.label}</h3>
                <p className="text-sky-100 text-sm mb-5 leading-relaxed">
                  Open the full county toolset to cross-check notice rules for other locations.
                </p>
                <Link
                  to={report.ctas.compliance_hub.path}
                  className="no-underline inline-flex w-full items-center justify-between rounded-xl bg-white text-sky-800 px-4 py-3 font-black"
                >
                  Open Compliance Hub <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              {!!checklist.length && (
                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-black text-slate-900 mb-3 flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-slate-700" /> Pre-Send Checklist
                  </h3>
                  <div className="space-y-2">
                    {checklist.slice(0, 4).map((item, idx) => (
                      <p key={`${item}-${idx}`} className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
                        {item}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-2">
                  <Share2 className="h-4 w-4 text-slate-700" /> Share This Report
                </h3>
                <div className="flex items-center gap-2 mb-4">
                  <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                    <FacebookIcon size={34} round />
                  </FacebookShareButton>
                  <TwitterShareButton url={shareUrl} title={shareText}>
                    <TwitterIcon size={34} round />
                  </TwitterShareButton>
                  <WhatsappShareButton url={shareUrl} title={shareText}>
                    <WhatsappIcon size={34} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton
                    url={shareUrl}
                    title={`Rent Increase Notice in ${countyName} | RentSmart.ie`}
                    summary={shareText}
                    source="RentSmart.ie"
                  >
                    <LinkedinIcon size={34} round />
                  </LinkedinShareButton>
                </div>
                {!!report.ai_enrichment?.social_share_snippets?.length && (
                  <div className="space-y-2">
                    {report.ai_enrichment.social_share_snippets.map((item, idx) => (
                      <div key={`${item.label}-${idx}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                          {item.label}
                        </p>
                        <p className="text-sm text-slate-700">{item.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default RentIncreaseNoticeCountyPage;
